import { useEffect, useMemo } from 'react';

import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useMantineTheme } from 'ui';
import { useMediaQuery } from 'ui/Hooks';

import { screenBreakPoints } from '../config/screenBreakPoints';

const findBreakPoint = () => {
  const width = window.innerWidth;
  return screenBreakPoints.find(({ max, min }) => {
    return max > width && min <= width;
  })?.screen;
};
const screenBreakpointAtom = atom<number | undefined | string>(findBreakPoint());
screenBreakpointAtom.debugLabel = 'screenBreakpointAtom';
const initializedAtom = atom(false);
initializedAtom.debugLabel = 'initializedAtom';

const throttle = (cb: any, time: number) => {
  let isWaiting = false;
  return (...args: any) => {
    if (isWaiting) return;
    cb(...args);
    isWaiting = true;
    setTimeout(() => {
      isWaiting = false;
    }, time);
  };
};

export const useSetResizeListener = () => {
  const setScreenSize = useSetAtom(screenBreakpointAtom);
  const [hasListener, setListener] = useAtom(initializedAtom);

  useEffect(() => {
    const callback = () => {
      const size = findBreakPoint();
      setScreenSize(size);
    };

    if (!hasListener) {
      setListener(true);
      window.addEventListener('resize', throttle(callback, 250));
    } else {
      console.error('I already exist! 🙄');
    }
    return () => {
      window.removeEventListener('resize', callback);
      setListener(false);
    };
  }, []);
};

export const useBreakPoints = () => {
  const theme = useMantineTheme();
  const isXSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const isMediumScreen = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const isLargeScreen = useMediaQuery(`(max-width: ${theme.breakpoints.lg})`);
  const isLargeScreenOrLarger = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`);
  const isXLargeScreen = useMediaQuery(`(min-width: ${theme.breakpoints.xl})`);

  const screenType = useAtomValue(screenBreakpointAtom);

  const isDesktop = useMemo(() => screenType === 'desktop', [screenType]);
  const isTablet = useMemo(() => screenType === 'tablet', [screenType]);
  const isMobile = useMemo(() => screenType === 'mobile', [screenType]);

  return {
    isDesktop,
    isTablet,
    isMobile,
    isXSmallScreen,
    isSmallScreen,
    isMediumScreen,
    isLargeScreen,
    isXLargeScreen,
    isLargeScreenOrLarger,
  };
};
